import { Head, router, usePage } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import AuthLayout from '@/components/auth/AuthLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import FlashBanner from '@/components/ui/flash-banner';

type PageProps = {
  defaultEmail?: string;
};

export default function ForgotPassword({ defaultEmail }: PageProps) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { props } = usePage<{ flash?: { alert?: string; notice?: string } }>();
  const { flash = {} } = props;

  useEffect(() => {
    if (flash.alert) {
      setError(flash.alert as string);
      setNotice(null);
    } else if (flash.notice) {
      setNotice(flash.notice as string);
      setError(null);
    }
  }, [flash.alert, flash.notice]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNotice(null);
    setError(null);
    router.post(
      '/patients/password',
      { patient: { email } },
      {
        preserveScroll: true,
        onError: () => setError('Errore durante la richiesta'),
        onFinish: () => setIsSubmitting(false),
      }
    );
  };

  return (
    <AuthLayout>
      <Head title="Recupera password" />
      <div className="w-full max-w-md">
        <Card className="rounded-3xl border-white/20 bg-white/90 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45),0_25px_60px_-15px_rgba(120,64,255,0.25)]">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold tracking-tight text-slate-900">Password dimenticata?</CardTitle>
            <CardDescription className="text-base text-slate-600">
              Inserisci la tua email. Se è registrata, ti invieremo un link per reimpostare la password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notice && <FlashBanner message={notice} tone="success" className="mb-3" />}
            {error && <FlashBanner message={error} tone="error" className="mb-3" />}
            <form className="space-y-6" onSubmit={submit}>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="email"
                  className="bg-white/95 text-slate-900 placeholder:text-slate-500 focus:bg-white border-slate-200"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: 'var(--emovia-primary)' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia link'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  );
}
